// The MLS setup wizard's step order: property types first, so discovery re-runs with
// them before the listings are offered (commercial types are opt-in only).
export type SetupStep = 1 | 2 | 3 | 4;

export const SETUP_STEP_LABELS: readonly string[] = [
    "Property Types",
    "Select Listings",
    "Auto Pilot",
    "Auto-Video",
];

interface StepContext {
    hasImports: boolean;
    showAutopilotStep: boolean;
}

// After the types are saved and discovery re-run: the listings if any are
// importable, else Auto Pilot if it is offered, else finish (null).
export const stepAfterTypes = ({
    hasImports,
    showAutopilotStep,
}: StepContext): SetupStep | null =>
    hasImports ? 2 : showAutopilotStep ? 3 : null;

export const stepAfterListings = ({
    showAutopilotStep,
}: Pick<StepContext, "showAutopilotStep">): SetupStep | null =>
    showAutopilotStep ? 3 : null;

// Back from Auto Pilot lands on the listings, or on the types when the
// listings step was skipped for want of anything to import.
export const stepBeforeAutopilot = ({
    hasImports,
}: Pick<StepContext, "hasImports">): SetupStep => (hasImports ? 2 : 1);

// Same case- and separator-insensitive compare the api uses (propertyTypeKey).
const propertyTypeKey = (value: string): string =>
    value.toLowerCase().replace(/[^a-z0-9]/g, "");

// Discovery before the wizard put listings of not-yet-chosen types (Land, Farm)
// in Excluded, and a refresh never re-stages Excluded. Only a type ticked in the
// wizard marks an exclusion as Marabot's, so only those are brought back.
export const keysToRestore = (
    excluded: ReadonlyArray<{ ListingKey: string; listingData: { PropertyType?: string } }>,
    chosenTypes: ReadonlySet<string>,
    typesAtOpen: ReadonlySet<string>,
): string[] => {
    const openKeys = new Set(Array.from(typesAtOpen).map(propertyTypeKey));
    const newKeys = new Set(
        Array.from(chosenTypes)
            .map(propertyTypeKey)
            .filter((key) => !openKeys.has(key)),
    );
    return excluded
        .filter((c) => {
            const type = c.listingData.PropertyType;
            return !!type && newKeys.has(propertyTypeKey(type));
        })
        .map((c) => c.ListingKey);
};

// A refresh can change the discovered set; keep only the choices that still
// point at a listing in it.
export const keepKnownKeys = (
    keys: Set<string>,
    candidates: ReadonlyArray<{ ListingKey: string }>,
): Set<string> =>
    new Set(
        Array.from(keys).filter((key) =>
            candidates.some((c) => c.ListingKey === key),
        ),
    );
